import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Target, DollarSign, TrendingDown, AlertTriangle, Package, Factory, Truck, Recycle, CheckCircle, XCircle } from 'lucide-react';

const GRIAlignment = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchGRIReport = async () => {
    const data = await apiFetch(`${API}/gri/alignment-report`);
    if (data?._error) {
      console.error('Error fetching GRI report:', data.message);
      setLoading(false);
      return;
    }
    setReport(data);
    setLoading(false);
  };

   
  useEffect(() => {
    fetchGRIReport();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mb-4"></div>
        <p className="text-slate-600">Analyzing GRI alignment and financial risks...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center p-8">
        <AlertTriangle className="mx-auto text-red-500 mb-4" size={48} />
        <p className="text-slate-600">Unable to load GRI alignment report</p>
      </div>
    );
  }

  const { executive_summary, gap_analyses, financial_risks, financial_exposure_summary, value_chain_analysis, priority_actions } = report;

  const getAlignmentColor = (score) => {
    if (score >= 80) return { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-500' };
    if (score >= 60) return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-500' };
    return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-500' };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const alignmentColors = getAlignmentColor(executive_summary.gri_alignment_score);

  const valueChainIcons = {
    upstream: <Package className="text-blue-600" size={24} />,
    operations: <Factory className="text-green-600" size={24} />,
    downstream: <Truck className="text-orange-600" size={24} />,
    end_of_life: <Recycle className="text-purple-600" size={24} />
  };

  return (
    <div className="space-y-6" data-testid="gri-alignment-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-lg p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Target size={32} />
          <div>
            <h1 className="text-2xl font-bold">GRI Alignment Agent</h1>
            <p className="text-purple-100 text-sm">Financial Risk Analysis & Value Chain Impact Assessment</p>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* GRI Alignment Score */}
        <div className={`bg-white rounded-lg shadow-lg border-l-4 ${alignmentColors.border} p-6`}>
          <h3 className="text-sm font-semibold text-slate-600 uppercase mb-2">GRI Alignment Score</h3>
          <div className={`text-5xl font-bold ${alignmentColors.text} mb-2`}>
            {executive_summary.gri_alignment_score}%
          </div>
          <div className="text-xs text-slate-600">
            {executive_summary.complete_disclosures}/{executive_summary.total_indicators_analyzed} complete disclosures
          </div>
        </div>

        {/* Total Financial Exposure */}
        <div className="bg-white rounded-lg shadow-lg border-l-4 border-red-500 p-6">
          <h3 className="text-sm font-semibold text-slate-600 uppercase mb-2">Total Financial Exposure</h3>
          <div className="text-3xl font-bold text-red-600 mb-2">
            {formatCurrency(executive_summary.total_financial_exposure_usd)}
          </div>
          <div className="text-xs text-slate-600">
            Across all risk categories
          </div>
        </div>

        {/* Critical Gaps */}
        <div className="bg-white rounded-lg shadow-lg border-l-4 border-orange-500 p-6">
          <h3 className="text-sm font-semibold text-slate-600 uppercase mb-2">Critical Data Gaps</h3>
          <div className="text-5xl font-bold text-orange-600 mb-2">
            {executive_summary.critical_gaps}
          </div>
          <div className="text-xs text-slate-600">
            Requiring immediate attention
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-slate-200">
          <div className="flex gap-1 p-1 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'financial', label: 'Financial Risks' },
              { id: 'gaps', label: 'Data Gaps' },
              { id: 'valuechain', label: 'Value Chain' },
              { id: 'actions', label: 'Priority Actions' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'bg-purple-600 text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-4">GRI Compliance Overview</h2>
                <div className="bg-purple-50 border-l-4 border-purple-600 p-4 rounded mb-6">
                  <p className="text-sm text-purple-900">
                    Your organization shows <strong className={alignmentColors.text}>{executive_summary.gri_alignment_score}% alignment</strong> with 
                    GRI Universal Standards. We've analyzed <strong>{executive_summary.total_indicators_analyzed} indicators</strong> and identified{' '}
                    <strong className="text-red-600">{formatCurrency(executive_summary.total_financial_exposure_usd)}</strong> in potential 
                    financial exposure across the value chain.
                  </p>
                </div>
              </div>

              {/* Financial Exposure Breakdown */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Financial Exposure Breakdown</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown className="text-yellow-600" size={20} />
                      <h4 className="font-semibold text-yellow-900">Opportunity Cost</h4>
                    </div>
                    <div className="text-2xl font-bold text-yellow-800 mb-1">
                      {formatCurrency(financial_exposure_summary.total_opportunity_cost)}
                    </div>
                    <p className="text-xs text-yellow-700">
                      Lost revenue from green financing, ESG investor premium, and carbon credits
                    </p>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="text-red-600" size={20} />
                      <h4 className="font-semibold text-red-900">Sunk Cost</h4>
                    </div>
                    <div className="text-2xl font-bold text-red-800 mb-1">
                      {formatCurrency(financial_exposure_summary.total_sunk_cost)}
                    </div>
                    <p className="text-xs text-red-700">
                      ESG investments not yielding measurable results
                    </p>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="text-orange-600" size={20} />
                      <h4 className="font-semibold text-orange-900">Compliance Risk</h4>
                    </div>
                    <div className="text-2xl font-bold text-orange-800 mb-1">
                      {formatCurrency(financial_exposure_summary.total_compliance_risk)}
                    </div>
                    <p className="text-xs text-orange-700">
                      Potential regulatory penalties (CSRD, GDPR, sector-specific)
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Factory className="text-blue-600" size={20} />
                      <h4 className="font-semibold text-blue-900">Operational Inefficiency</h4>
                    </div>
                    <div className="text-2xl font-bold text-blue-800 mb-1">
                      {formatCurrency(financial_exposure_summary.total_operational_risk)}
                    </div>
                    <p className="text-xs text-blue-700">
                      Excess resource consumption, waste, energy costs
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Financial Risks Tab */}
          {activeTab === 'financial' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Financial Risk Quantification</h2>
                <p className="text-sm text-slate-600 mb-4">
                  Detailed breakdown of financial exposure by indicator with calculation methodology
                </p>
              </div>

              <div className="space-y-3">
                {financial_risks && financial_risks.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="bg-white border-2 border-slate-200 rounded-lg p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900 text-lg">{item.indicator}</h3>
                        <span className="text-xs font-semibold px-2 py-1 bg-purple-100 text-purple-800 rounded mt-1 inline-block">
                          {item.category}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-slate-500">Total Exposure</div>
                        <div className="text-xl font-bold text-red-600">
                          {formatCurrency(item.total_exposure_usd)}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {item.risks.map((risk, ridx) => (
                        <div key={ridx} className="bg-slate-50 border-l-4 border-red-400 p-3 rounded">
                          <div className="flex items-start justify-between mb-2">
                            <div className="font-semibold text-slate-900 text-sm">
                              {risk.risk_type.replace(/_/g, ' ').toUpperCase()}
                            </div>
                            <div className="text-lg font-bold text-red-600">
                              {formatCurrency(risk.amount_usd)}
                            </div>
                          </div>
                          <p className="text-sm text-slate-700 mb-2">{risk.description}</p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-slate-500">Basis:</span>
                              <span className="text-slate-700 ml-1">{risk.calculation_basis}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Timeframe:</span>
                              <span className="text-slate-700 ml-1">{risk.timeframe}</span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-slate-500">Probability:</span>
                              <span className="text-slate-700 ml-1">{risk.probability}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Data Gaps Tab */}
          {activeTab === 'gaps' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">GRI Data Gap Analysis</h2>
                <p className="text-sm text-slate-600 mb-4">
                  Expected vs actual data collected - identifies compliance risks
                </p>
              </div>

              <div className="space-y-3">
                {gap_analyses && gap_analyses.filter(g => g.gap_analysis.total_gaps > 0).slice(0, 15).map((item, idx) => {
                  const indicator = item.indicator;
                  const gaps = item.gap_analysis;
                  
                  return (
                    <div key={idx} className="bg-white border-2 border-slate-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">{indicator.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-600">{indicator.code}</span>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                              gaps.data_quality_score >= 80 ? 'bg-green-100 text-green-800' :
                              gaps.data_quality_score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              Quality: {gaps.data_quality_score.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-orange-600">{gaps.total_gaps}</div>
                          <div className="text-xs text-slate-500">gaps</div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {gaps.gaps_identified.map((gap, gidx) => (
                          <div key={gidx} className="bg-orange-50 border-l-4 border-orange-500 p-3 rounded">
                            <div className="flex items-start gap-2 mb-2">
                              {gap.severity === 'critical' ? <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} /> : <AlertTriangle className="text-orange-600 flex-shrink-0 mt-0.5" size={16} />}
                              <div className="flex-1">
                                <div className="text-sm font-semibold text-orange-900 mb-1">{gap.description}</div>
                                <div className="text-xs text-orange-700 mb-1">{gap.gri_requirement}</div>
                                <div className="bg-white border border-orange-200 rounded p-2 mt-2">
                                  <div className="text-xs text-slate-700">
                                    <strong className="text-orange-800">Remediation:</strong> {gap.remediation}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Value Chain Tab */}
          {activeTab === 'valuechain' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Value Chain Impact Analysis</h2>
                <p className="text-sm text-slate-600 mb-4">
                  Distribution of ESG impacts across your value chain stages
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(value_chain_analysis).map(([stage, indicators]) => {
                  const stageLabels = {
                    upstream: 'Upstream (Suppliers & Raw Materials)',
                    operations: 'Operations (Own Facilities)',
                    downstream: 'Downstream (Distribution & Customers)',
                    end_of_life: 'End-of-Life (Product Disposal)'
                  };

                  const stageColors = {
                    upstream: 'blue',
                    operations: 'green',
                    downstream: 'orange',
                    end_of_life: 'purple'
                  };

                  const color = stageColors[stage];
                  const exposure = financial_exposure_summary.by_value_chain_stage[stage] || 0;

                  return (
                    <div key={stage} className={`bg-white border-2 border-${color}-300 rounded-lg p-5`}>
                      <div className="flex items-center gap-3 mb-4">
                        {valueChainIcons[stage]}
                        <div>
                          <h3 className="font-semibold text-slate-900">{stageLabels[stage]}</h3>
                          <div className="text-sm text-slate-600">{indicators.length} indicators affected</div>
                        </div>
                      </div>

                      <div className={`bg-${color}-50 rounded-lg p-3 mb-3`}>
                        <div className="text-xs text-slate-600 mb-1">Financial Exposure</div>
                        <div className={`text-2xl font-bold text-${color}-800`}>
                          {formatCurrency(exposure)}
                        </div>
                      </div>

                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {indicators.slice(0, 10).map((ind, idx) => (
                          <div key={idx} className={`text-xs p-2 bg-${color}-50 rounded`}>
                            {typeof ind === 'string' ? ind : ind.indicator}
                          </div>
                        ))}
                        {indicators.length > 10 && (
                          <div className="text-xs text-slate-500 italic">
                            + {indicators.length - 10} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Priority Actions Tab */}
          {activeTab === 'actions' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Priority Action Items</h2>
                <p className="text-sm text-slate-600 mb-4">
                  Ranked actions to improve GRI alignment and reduce financial exposure
                </p>
              </div>

              <div className="space-y-3">
                {priority_actions && priority_actions.map((action, idx) => {
                  const priorityColors = {
                    'Critical': 'red',
                    'High': 'orange',
                    'Medium': 'yellow'
                  };
                  const color = priorityColors[action.priority] || 'blue';

                  return (
                    <div key={idx} className={`bg-white border-2 border-${color}-400 rounded-lg p-5`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-full bg-${color}-600 text-white flex items-center justify-center font-bold`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900 text-lg">{action.action}</h3>
                            <span className={`px-2 py-1 bg-${color}-600 text-white text-xs font-bold rounded-full`}>
                              {action.priority}
                            </span>
                          </div>
                          <div className="text-sm text-slate-600">{action.timeframe}</div>
                        </div>
                      </div>

                      <div className={`bg-${color}-50 rounded-lg p-3 mb-3`}>
                        <div className="text-xs font-semibold text-slate-700 mb-1">Expected Impact:</div>
                        <div className={`text-sm text-${color}-900`}>{action.impact}</div>
                      </div>

                      <div>
                        <div className="text-xs font-semibold text-slate-700 mb-2">Affected Indicators:</div>
                        <div className="space-y-1">
                          {action.indicators_affected.slice(0, 5).map((ind, iidx) => (
                            <div key={iidx} className="text-xs p-2 bg-slate-50 rounded flex items-center gap-2">
                              <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                              <span className="text-slate-700">{ind}</span>
                            </div>
                          ))}
                          {action.indicators_affected.length > 5 && (
                            <div className="text-xs text-slate-500 italic pl-6">
                              + {action.indicators_affected.length - 5} more
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GRIAlignment;
